"use client";

import React from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { OakFlex, OakLoadingSpinner } from "@oaknational/oak-components";

import { useGoogleClassroomAddonStore } from "@/store";
import { AuthCookieKeys } from "@/types/ui";

type Props = {
  children: React.ReactNode;
  verifySessionAction: () => Promise<{
    authenticated: boolean;
    session?: string;
    accessToken?: string;
    userProfilePicUrl?: string;
    loginHint?: string;
  }>;
  signInUrl: string;
  cookieKeys?: [
    AuthCookieKeys.AccessToken | AuthCookieKeys.PupilAccessToken,
    AuthCookieKeys.Session | AuthCookieKeys.PupilSession,
  ];
  onVerifySuccess?: () => void | Promise<void>;
};
export const WithGoogleClassroomAuth: React.FC<Props> = ({
  children,
  verifySessionAction,
  signInUrl,
  cookieKeys = [AuthCookieKeys.AccessToken, AuthCookieKeys.Session],
  onVerifySuccess,
}) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [hasCheckedAuth, setHasCheckedAuth] = React.useState(false);
  const [accessTokenCookieKey, sessionCookieKey] = cookieKeys;

  const setUser = useGoogleClassroomAddonStore((state) => state.setUser);
  const setGoogleLoginHint = useGoogleClassroomAddonStore(
    (state) => state.setGoogleLoginHint,
  );

  const updateCookies = async (session: string, accessToken: string) => {
    await window?.cookieStore?.set({
      name: sessionCookieKey,
      value: session,
      partitioned: true,
      sameSite: "none",
      path: "/",
    });
    await window?.cookieStore?.set({
      name: accessTokenCookieKey,
      value: accessToken,
      partitioned: true,
      sameSite: "none",
      path: "/",
    });
  };

  const checkAuthenticated = async () => {
    const redirectToSignIn = () => {
      const currentParams = searchParams?.toString() ?? "";
      const separator = signInUrl.includes("?") ? "&" : "?";
      const targetUrl = currentParams
        ? `${signInUrl}${separator}${currentParams}`
        : signInUrl;
      try {
        router.push(targetUrl);
      } catch (error) {
        console.error("Router navigation failed:", error);
        window.location.href = targetUrl;
      }
    };

    try {
      if (!window?.cookieStore) {
        redirectToSignIn();
        return;
      }

      const session = (await window.cookieStore.get(sessionCookieKey))?.value;
      const accessToken = (await window.cookieStore.get(accessTokenCookieKey))
        ?.value;

      if (!session || !accessToken) {
        redirectToSignIn();
        return;
      }

      const {
        authenticated,
        session: latestSession,
        accessToken: latestToken,
        userProfilePicUrl,
        loginHint,
      } = await verifySessionAction();

      if (!authenticated) {
        redirectToSignIn();
        return;
      }

      setUser(userProfilePicUrl);
      if (loginHint) setGoogleLoginHint(loginHint);
      setHasCheckedAuth(true);
      if (latestSession && latestToken) {
        await updateCookies(latestSession, latestToken);
      }
      if (onVerifySuccess) await onVerifySuccess();
    } catch (error) {
      console.error("Authentication check failed:", error);
      redirectToSignIn();
    }
  };

  React.useEffect(() => {
    checkAuthenticated();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!hasCheckedAuth)
    return (
      <OakFlex $justifyContent={"center"} $alignItems={"center"}>
        <OakLoadingSpinner $width={"spacing-100"} $color={"icon-brand"} />
      </OakFlex>
    );
  return <>{children}</>;
};
